import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface Props {
  label: string;
  reference: string;
  days: 15 | 90;
  /** Optional narrative the QPPV can add while real data is wired up. */
  commentary?: string;
}

/**
 * Placeholder bar-chart-style widget for 15-day and 90-day ICSR reporting
 * timeliness required by GVP Module II Section II.B.4.6. Renders a static
 * illustrative distribution until live PVCase aggregates are available.
 */
export function TimelinessChart({ label, reference, days, commentary }: Props) {
  // Placeholder bars (quarter-on-quarter % on-time). Replaced by live data once the endpoint lands.
  const bars = days === 15
    ? [
        { q: 'Q1', value: 92 },
        { q: 'Q2', value: 95 },
        { q: 'Q3', value: 97 },
        { q: 'Q4', value: 96 },
      ]
    : [
        { q: 'Q1', value: 88 },
        { q: 'Q2', value: 90 },
        { q: 'Q3', value: 93 },
        { q: 'Q4', value: 94 },
      ];

  const target = 95;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{label}</CardTitle>
          <Badge variant="secondary">{reference}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Auto-populate from PVCase data (endpoint TBD). Target {target}% on-time submission.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-3 pt-2">
          {bars.map((b) => (
            <div key={b.q} className="flex flex-1 flex-col items-center gap-1">
              <div className="flex h-28 w-full items-end rounded-sm bg-muted">
                <div
                  className={`w-full rounded-sm ${b.value >= target ? 'bg-emerald-500/70' : 'bg-amber-500/70'}`}
                  style={{ height: `${b.value}%` }}
                />
              </div>
              <div className="text-xs font-medium">{b.value}%</div>
              <div className="text-[10px] text-muted-foreground">{b.q}</div>
            </div>
          ))}
        </div>
        {commentary && (
          <p className="mt-3 rounded-md border border-dashed bg-muted/40 p-2 text-xs">
            {commentary}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
